import { Component, OnInit } from '@angular/core';
import { CategoryDropdownItem, IconSize } from '@predictiveindex/tapestry';
import { firstValueFrom } from 'rxjs';
import { LocalStorageService, SessionStorageService } from '../../../cache';
import { CompanyClassificationType } from '../../../constants-types';
import { Company } from '../../../core-data-model';
import {
    CompaniesDataService,
    DemoAccountsRepositoryService,
    HashService
} from '../../../core-services';
import {
    InitDataService,
    Logger,
    UserProfileSettingsService
} from '../../../system-services';
import { TranslationService } from '../../../translations';

@Component({
    selector: 'account-selector',
    templateUrl: './account-selector.component.html',
    styleUrls: ['./account-selector.component.less'],
    standalone: false
})
export class AccountSelectorComponent implements OnInit {
    labelText: string;
    selectedCompany: Company;
    IconSize = IconSize;
    loading = true;
    companies: Company[] = [];
    items: CategoryDropdownItem<Company>[] = [];

    constructor(
        public TR: TranslationService,
        public companyData: CompaniesDataService,
        public userProfile: UserProfileSettingsService,
        public session: SessionStorageService,
        private initData: InitDataService,
        private hashService: HashService,
        private localStorage: LocalStorageService,
        private demoAccountsRepositoryService: DemoAccountsRepositoryService,
        private logger: Logger
    ) {}

    async ngOnInit() {
        await this.companyData.companiesPromise;
        this.companyData.companies$.pipe().subscribe((companies) => {
            this.companies = companies.toArray();
            this.hydrateCompanyList(this.companies);
            this.loading = false;
        });
    }

    getDefaultCompany(companyId: string): Company {
        if (companyId) {
            return this.companies.find((company) => company.id === companyId);
        }
    }

    changeCompany(company: Company) {
        if (company.id !== this.userProfile.currentCompany) {
            this.userProfile.setCurrentCompany(company.id).then(async () => {
                this.session.clearAll();
                await this.storeSelectedCompanyId(company.id);
                window.location.href = '/';
            });
        }
    }

    async storeSelectedCompanyId(companyId: string) {
        const currentUserEmail = this.initData.user.email.toLowerCase();
        const hashedEmail =
            await this.hashService.computeHashHex(currentUserEmail);
        this.localStorage.setItem(`${hashedEmail}-currentCompany`, companyId);
    }

    handleSelection(item: CategoryDropdownItem<Company>) {
        this.selectedCompany = item.value;
        this.changeCompany(item.value);
    }

    private hydrateCompanyList(companies: Company[]) {
        const defaultCompany = companies.find((company) => company.isDefault);

        const defaultCompanyItems: CategoryDropdownItem<Company>[] = [
            {
                title: defaultCompany.name,
                value: defaultCompany
            }
        ];
        const demoAccountsItems: CategoryDropdownItem<Company>[] = [];
        const workshopAccountsItems: CategoryDropdownItem<Company>[] = [];
        const customerAccountsItems: CategoryDropdownItem<Company>[] = [];

        companies.forEach((company) => {
            if (company.isDefault) {
                return;
            }

            let titleKey: string;
            let targetItemsArray: CategoryDropdownItem<Company>[];

            switch (company.companyClassificationType) {
                case CompanyClassificationType.Seller:
                    defaultCompanyItems.push({
                        title: company.name,
                        value: company
                    });
                    return;
                case CompanyClassificationType.DynamicDemo:
                    titleKey = 'globalnav.account-selector.demo-accounts-title';
                    targetItemsArray = demoAccountsItems;
                    break;
                case CompanyClassificationType.Workshop:
                    titleKey =
                        'globalnav.account-selector.workshop-accounts-title';
                    targetItemsArray = workshopAccountsItems;
                    break;
                default:
                    titleKey =
                        'globalnav.account-selector.customer-accounts-title';
                    targetItemsArray = customerAccountsItems;
            }

            const existingItem = targetItemsArray.find(
                (item) => item.title === this.TR.key(titleKey)
            );

            if (existingItem) {
                existingItem.children.push({
                    title: company.name,
                    value: company
                });
            } else {
                targetItemsArray.push({
                    title: this.TR.key(titleKey),
                    children: [
                        {
                            title: company.name,
                            value: company
                        }
                    ]
                });
            }
        });

        this.items = [
            ...defaultCompanyItems,
            ...demoAccountsItems,
            ...workshopAccountsItems,
            ...customerAccountsItems
        ];

        const selectedCompany = this.getDefaultCompany(
            this.userProfile.currentCompany
        );

        if (selectedCompany) {
            this.selectedCompany = selectedCompany;
        }

        this.updateDemoaccountLastAccessed(selectedCompany);
    }

    private async updateDemoaccountLastAccessed(company: Company) {
        if (
            company.companyClassificationType ===
                CompanyClassificationType.DynamicDemo ||
            company.companyClassificationType ===
                CompanyClassificationType.Workshop
        ) {
            try {
                await firstValueFrom(
                    this.demoAccountsRepositoryService.updateDemoAccountLastAccessed(
                        company.id
                    )
                );
                this.logger.logInfo(
                    'Last accessed updated successfully',
                    company
                );
            } catch (error) {
                this.logger.logError('Failed to update last accessed', company);
            }
        }
    }
}
